import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  AccountBalance,
  CalendarToday,
  Receipt as ReceiptIcon,
  ShowChart as TrendingUpIcon,
  ArrowBack,
  BarChart,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { BarChart3 } from 'lucide-react';

// Quick Action Card Component
const QuickActionCard = ({ title, description, icon: Icon, color, onClick, disabled = false }) => (
  <Card 
    sx={{ 
      height: '100%', 
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      transition: 'all 0.2s',
      '&:hover': disabled ? {} : {
        transform: 'translateY(-2px)',
        boxShadow: 3
      }
    }}
    onClick={disabled ? undefined : onClick}
  >
    <CardContent sx={{ textAlign: 'center', py: 3 }}>
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          backgroundColor: `${color}.light`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          mx: 'auto'
        }}
      >
        <Icon sx={{ fontSize: 30, color: `${color}.main` }} />
      </Box>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </CardContent>
  </Card>
);

const ReportsDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const reportActions = [
    {
      title: 'Transactions',
      description: 'View all income and expenditure transactions',
      icon: Assessment,
      color: 'primary',
      onClick: () => navigate('/reports/transaction')
    },
    {
      title: 'Profit & Loss',
      description: 'P&L statement and profitability analysis',
      icon: TrendingUp,
      color: 'success',
      onClick: () => navigate('/reports/profit-loss')
    },
    {
      title: 'Cashflow',
      description: 'Cash flow analysis and forecasting',
      icon: AccountBalance,
      color: 'info',
      onClick: () => navigate('/reports/cashflow')
    },
    {
      title: 'Tax Report',
      description: 'Tax calculations and submissions',
      icon: CalendarToday,
      color: 'warning',
      onClick: () => navigate('/reports/tax')
    },
    {
      title: 'VAT Returns',
      description: 'VAT reporting and submissions',
      icon: ReceiptIcon,
      color: 'secondary',
      onClick: () => navigate('/reports/vat')
    },
    {
      title: 'Aged Payables',
      description: 'Outstanding bills by aging period',
      icon: ReceiptIcon,
      color: 'error',
      onClick: () => navigate('/reports/aged-payables')
    },
    {
      title: 'Supplier Spending',
      description: 'Analytics and trends by supplier',
      icon: BarChart,
      color: 'primary',
      onClick: () => navigate('/reports/supplier-spending')
    },
    {
      title: 'Efficiency Analyser',
      description: 'Analyse team productivity, chair utilisation, and revenue per hour',
      icon: SpeedIcon,
      color: 'success',
      onClick: () => navigate('/reports/efficiency')
    }
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header with Back Button */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/dashboard')}
          startIcon={<ArrowBack />}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChart3 size={32} /> Business Reports
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive reports, tax calculations, profit & loss, and analytics
          </Typography>
        </Box>
      </Box>

      {/* Info Banner */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>Tip:</strong> Regular financial reporting helps you make informed business decisions. 
          Review your P&L monthly and keep your tax reports up to date.
        </Typography>
      </Alert>

      {/* Report Cards Grid */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Financial Reports
        </Typography>
        <Grid container spacing={3}>
          {reportActions.slice(0, 4).map((action, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <QuickActionCard {...action} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Supplier & VAT Reports */}
<Box sx={{ mb: 4 }}>
  <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
    Supplier & VAT Reports
  </Typography>
  <Grid container spacing={3}>
    {reportActions.slice(4, 7).map((action, index) => (
      <Grid item xs={12} sm={6} md={4} key={index}>
        <QuickActionCard {...action} />
      </Grid>
    ))}
  </Grid>
</Box>

{/* Analytics */}
<Box sx={{ mb: 4 }}>
  <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
    Analytics
  </Typography>
  <Grid container spacing={3}>
    {reportActions.slice(7).map((action, index) => (
      <Grid item xs={12} sm={6} md={4} key={index}>
        <QuickActionCard {...action} />
      </Grid>
    ))}
  </Grid>
</Box>

      {/* Help Section */}
      <Paper sx={{ p: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <Typography variant="h6" gutterBottom>
          Need Help with Reports? 📊
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Understanding your financial reports is crucial for business success. Each report provides 
          different insights into your salon's performance.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" color="inherit" size="small">
            Report Guide
          </Button>
          <Button variant="outlined" color="inherit" size="small">
            Video Tutorials
          </Button>
          <Button variant="outlined" color="inherit" size="small">
            Contact Support
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportsDashboard;